import { useEffect, useState } from 'react';
import { Trees, Loader2 } from 'lucide-react';
import irrigationNetworkService, {
  type IrrigationNode,
  type OrchardAreaOption,
  type OrchardBlockOption,
} from '../../../services/irrigationNetwork';
import { formatNum, toNum } from '@/utils/numeric';

interface Props {
  node: IrrigationNode;
  editable: boolean;
  /** Both ids move together — picking a block clears an area outside it. */
  onChange: (next: { block_id: number | null; cultivar_area_id: number | null }) => void;
}

/**
 * Where in the orchard a node sits.
 *
 * Distinct from the relay's drawn zone, and deliberately so. A zone is an
 * outline somebody put around a valve's wetted area; it hangs off the relay, so
 * it exists only where hardware exists and says nothing about what is planted
 * under it. A cultivar area carries the cultivar, the tree count and the
 * spacings — and a sensor, a tank or a junction has one of those and no relay
 * at all.
 *
 * The two ids are stored as a pair rather than the block being derived from the
 * area. A block is meaningful on its own (a valve standing in a block nobody
 * has subdivided yet), and the server re-checks that the pair agrees on every
 * write, which is where an area since moved between blocks is caught.
 *
 * Saved with the rest of the drawer, not on selection: these are columns on the
 * node like its name and elevation, not a device link that takes effect the
 * moment it is made.
 */
export default function OrchardAreaPicker({ node, editable, onChange }: Props) {
  const [blocks, setBlocks] = useState<OrchardBlockOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    irrigationNetworkService.listOrchardAreas(node.farm_id)
      .then((b) => { if (!cancelled) { setBlocks(b); setFailed(false); } })
      .catch(() => { if (!cancelled) { setBlocks([]); setFailed(true); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [node.farm_id]);

  const block = blocks.find(b => b.id === node.block_id) ?? null;
  // Areas of the chosen block; with no block chosen, every area on the farm, so
  // picking the area alone is enough and the block follows from it.
  const areas: OrchardAreaOption[] = block
    ? block.cultivar_areas
    : blocks.flatMap(b => b.cultivar_areas);
  const area = areas.find(a => a.id === node.cultivar_area_id)
    ?? blocks.flatMap(b => b.cultivar_areas).find(a => a.id === node.cultivar_area_id)
    ?? null;

  const linkedButMissing =
    (node.block_id !== null && !block) || (node.cultivar_area_id !== null && !area);

  return (
    <div className="mt-2 border rounded bg-gray-50 text-xs">
      <div className="px-2 py-1.5 border-b font-semibold flex items-center gap-1">
        <Trees className="w-3 h-3" /> Farm area
      </div>

      <div className="p-2 space-y-2">
        {loading && (
          <div className="text-gray-500 flex items-center gap-1">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading blocks…
          </div>
        )}
        {failed && <div className="text-amber-700">Could not load this farm&rsquo;s blocks.</div>}

        {!loading && !failed && blocks.length === 0 && (
          <div className="text-gray-500">
            This farm has no blocks mapped yet — draw them under Farms first.
          </div>
        )}

        {!loading && blocks.length > 0 && (
          <>
            <label className="block">
              <span className="text-gray-600">Block</span>
              <select
                className="input mt-0.5"
                disabled={!editable}
                value={node.block_id ?? ''}
                onChange={(e) => {
                  const next = e.target.value === '' ? null : Number(e.target.value);
                  // An area outside the new block would be a pair that
                  // contradicts itself, which the server refuses — so it goes
                  // in the same change rather than on the failed save.
                  const keep = next === null
                    || (area !== null && area.block_id === next);
                  onChange({ block_id: next, cultivar_area_id: keep ? node.cultivar_area_id : null });
                }}
              >
                <option value="">— not attributed —</option>
                {blocks.map(b => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                    {toNum(b.area_hectares) !== null ? ` · ${formatNum(b.area_hectares, 2)} ha` : ''}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="text-gray-600">Cultivar area</span>
              <select
                className="input mt-0.5"
                disabled={!editable || areas.length === 0}
                value={node.cultivar_area_id ?? ''}
                onChange={(e) => {
                  const id = e.target.value === '' ? null : Number(e.target.value);
                  const picked = id === null ? null : areas.find(a => a.id === id) ?? null;
                  // The block comes with it, so choosing an area alone is a
                  // complete answer.
                  onChange({
                    block_id: picked ? picked.block_id : node.block_id,
                    cultivar_area_id: id,
                  });
                }}
              >
                <option value="">
                  {areas.length === 0 ? '— this block has no cultivar areas —' : '— block only —'}
                </option>
                {areas.map(a => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                    {a.cultivar_name ? ` · ${a.cultivar_name}` : ''}
                    {toNum(a.area_hectares) !== null ? ` · ${formatNum(a.area_hectares, 2)} ha` : ''}
                  </option>
                ))}
              </select>
            </label>

            {area && (
              <div className="text-gray-600">
                {area.tree_count !== null && <>{area.tree_count} trees</>}
                {area.irrigation_zone && (
                  // Free text typed on the cultivar area long before any of
                  // this existed. Shown because a grower may have written the
                  // valve's name in it — never matched on.
                  <> · noted zone &ldquo;{area.irrigation_zone}&rdquo;</>
                )}
              </div>
            )}

            {linkedButMissing && (
              <div className="text-amber-700">
                This node points at a block or area that is no longer active on
                this farm. Choose another, or leave it unattributed.
              </div>
            )}

            {editable && (
              <div className="text-gray-500">
                Saved with the rest of this node — press Save below.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
